import axios, { type AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { caseKeys } from "src/query-keys";
import type { APIError } from "src/types";
import type { CaseBase, CaseInfo } from "src/types/case";

export type CaseCopyMutationData = { caseName: string };
export type CaseShareMutationData = { email: string };

export function useCopyCase(caseId: number) {
  const queryClient = useQueryClient();
  async function mutationFn(caseName: string) {
    await axios.post(`/planning-api/cases/${caseId}/copyWithScenarios`, { caseName });
  }
  return useMutation<void, AxiosError<APIError[]>, string>(mutationFn, {
    onSettled: async () => {
      await queryClient.invalidateQueries(caseKeys.all);
    },
  });
}

export function useCaseInfo(caseId: number | string) {
  async function queryFn(caseId: number | string): Promise<CaseInfo> {
    const response = await axios.get(`/planning-api/cases/${caseId}/info`);
    return response.data;
  }
  return useQuery<CaseInfo, AxiosError<APIError[]>>(caseKeys.info(caseId), () => queryFn(caseId));
}

export type CaseShareRevokeMutationData = { email?: string; leaving?: boolean };

export function useCaseShare(caseId: number) {
  const queryClient = useQueryClient();
  async function mutationFn(email: string) {
    await axios.post(`/planning-api/cases/${caseId}/sharing`, { email });
    await queryClient.invalidateQueries(caseKeys.detail(caseId));
  }
  return useMutation<void, AxiosError<APIError[]>, string>(mutationFn, {
    onSuccess: (_data, email) => {
      queryClient.setQueryData<CaseBase[]>(caseKeys.all, (old = []) =>
        old.map((c) => {
          if (c.id !== caseId) return c;
          const alreadyShared = c.sharedEmailList.includes(email);
          return alreadyShared ? c : { ...c, sharedEmailList: [...c.sharedEmailList, email] };
        })
      );
    },
  });
}

export function useCaseShareRevoke(caseId: number) {
  const queryClient = useQueryClient();
  async function mutationFn({ email }: CaseShareRevokeMutationData) {
    await axios.post(`/planning-api/cases/${caseId}/unsharing`, { email });
    await queryClient.invalidateQueries(caseKeys.detail(caseId));
  }
  return useMutation<void, AxiosError<APIError[]>, CaseShareRevokeMutationData>(mutationFn, {
    onSuccess: (_data, { email, leaving }) => {
      queryClient.invalidateQueries(caseKeys.all);
      queryClient.setQueryData<CaseBase[]>(caseKeys.all, (old = []) => {
        if (leaving) {
          return old.filter((c) => c.id !== caseId);
        }
        return old.map((c) => {
          if (c.id !== caseId) return c;
          return { ...c, sharedEmailList: email === undefined ? [] : c.sharedEmailList.filter((e) => e !== email) };
        });
      });
    },
  });
}
